import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search,
  Visibility,
  ArrowUpward,
  ArrowDownward,
  Delete,
  CalendarToday,
} from '@mui/icons-material';

interface Project {
  id: number;
  title: string;
  description?: string;
  customer_name: string;
  status: 'planning' | 'delivery_ready' | 'installation' | 'remedial' | 'remedial_resolved' | 'completed';
  budget: string | number;
  material_cost?: string | number;
  fitter_balance?: string | number;
  gas_fitter_balance?: string | number;
  actual_cost: string | number;
  start_date: string;
  end_date: string;
  delivery_date?: string;
  assigned_user: string | null;
  lead_id?: number;
  customer_id: number;
  order_number?: string;
  created_at: string;
  updated_at: string;
}

export default function Projects() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<number>(0); // Default to All Months
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear()); // Default to current year
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [users, setUsers] = useState<{ id: number; name: string }[]>([]);
  const [appointment, setAppointment] = useState({
    date: '',
    time: '',
    notes: '',
    type: 'Installation',
    assignedUser: '21'
  });

  useEffect(() => {
    loadProjects();
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/users', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadProjects = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const cacheBuster = `${Date.now()}_${Math.random()}`;
      const response = await fetch(`/api/projects?limit=1000&_t=${cacheBuster}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
        },
        cache: 'no-store',
      });

      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects || []);
      } else {
        console.error('Failed to load projects:', response.statusText);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter projects by selected month and year
  const getMonthProjects = () => {
    // If both are "All", return all projects
    if (selectedMonth === 0 && selectedYear === 0) {
      return projects;
    }

    return projects.filter(project => {
      const projectDate = new Date(project.created_at);
      const projectMonth = projectDate.getMonth() + 1; // 1-12
      const projectYear = projectDate.getFullYear();

      // All months but specific year
      if (selectedMonth === 0 && selectedYear !== 0) {
        return projectYear === selectedYear;
      }

      // Specific month but all years
      if (selectedMonth !== 0 && selectedYear === 0) {
        return projectMonth === selectedMonth;
      }

      // Both specific
      return projectMonth === selectedMonth && projectYear === selectedYear;
    });
  };

  const monthProjects = getMonthProjects();

  const filteredProjects = monthProjects.filter(project => {
    // Exclude remedial and remedial_resolved - they have their own page
    if (project.status === 'remedial' || project.status === 'remedial_resolved') {
      return false;
    }

    const matchesSearch =
      project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (project.order_number && project.order_number.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = statusFilter === 'all' ||
                          (statusFilter === 'active' && project.status !== 'completed') ||
                          project.status === statusFilter;

    return matchesSearch && matchesStatus;
  }).sort((a, b) => {
    if (!sortField) return 0;
    
    let aVal: any = '';
    let bVal: any = '';
    
    switch (sortField) {
      case 'title':
        aVal = a.title.toLowerCase();
        bVal = b.title.toLowerCase();
        break;
      case 'customer_name':
        aVal = a.customer_name.toLowerCase();
        bVal = b.customer_name.toLowerCase();
        break;
      case 'status':
        aVal = a.status;
        bVal = b.status;
        break;
      case 'budget':
        aVal = parseFloat(a.budget.toString());
        bVal = parseFloat(b.budget.toString());
        break;
      case 'start_date':
        aVal = new Date(a.start_date).getTime();
        bVal = new Date(b.start_date).getTime();
        break;
      case 'assigned_user':
        aVal = (a.assigned_user || '').toLowerCase();
        bVal = (b.assigned_user || '').toLowerCase();
        break;
      default:
        return 0;
    }
    
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const getStatusColor = (status: string) => {
    const colors: { [key: string]: { backgroundColor: string; color: string } } = {
      planning: { backgroundColor: '#ff9800', color: '#fff' },
      delivery_ready: { backgroundColor: '#3498DB', color: '#fff' },
      installation: { backgroundColor: '#9c27b0', color: '#fff' },
      remedial: { backgroundColor: '#f44336', color: '#fff' },
      remedial_resolved: { backgroundColor: '#4caf50', color: '#fff' },
      completed: { backgroundColor: '#4caf50', color: '#fff' },
    };
    return colors[status] || { backgroundColor: '#9e9e9e', color: '#fff' };
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount);
  };

  const getBudgetStatus = (budget: string | number, actual: string | number) => {
    const budgetNum = parseFloat(budget.toString());
    const actualNum = parseFloat(actual.toString());
    const percentage = (actualNum / budgetNum) * 100;
    if (percentage > 100) return 'Over Budget';
    if (percentage > 90) return 'Near Budget';
    return 'On Budget';
  };

  const handleProjectClick = (projectId: number) => {
    navigate(`/projects/${projectId}`);
  };

  const handleDeleteClick = (project: Project, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent row click
    setProjectToDelete(project);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (projectToDelete) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`/api/projects/${projectToDelete.id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (response.ok) {
          // Remove the project from local state
          setProjects(prev => prev.filter(p => p.id !== projectToDelete.id));
          setDeleteDialogOpen(false);
          setProjectToDelete(null);
          // Refresh the list
          await loadProjects();
        } else {
          console.error('Failed to delete project');
          alert('Failed to delete project. Please try again.');
        }
      } catch (error) {
        console.error('Error deleting project:', error);
        alert('An error occurred while deleting the project.');
      }
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleScheduleClick = (project: Project, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedProject(project);
    setAppointment({
      date: project.start_date ? project.start_date.split('T')[0] : '',
      time: '09:00',
      notes: '',
      type: 'Installation',
      assignedUser: '21'
    });
    setAppointmentDialogOpen(true);
  };

  const handleBookAppointment = async () => {
    if (selectedProject && appointment.date && appointment.time) {
      try {
        const assignedUserName = users.find(u => u.id === parseInt(appointment.assignedUser))?.name || 'Unknown User';
        const token = localStorage.getItem('token');

        // Create calendar event
        const calendarResponse = await fetch('/api/calendar/events', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            title: `${appointment.type} - ${selectedProject.customer_name}`,
            description: appointment.notes || `${appointment.type} for project: ${selectedProject.title}`,
            event_type: 'project',
            event_date: appointment.date,
            start_time: appointment.time,
            end_time: null,
            all_day: false,
            project_id: selectedProject.id,
            customer_id: selectedProject.customer_id || null,
            assigned_to: parseInt(appointment.assignedUser) || null,
            color: '#9c27b0'
          }),
        });

        if (calendarResponse.ok) {
          setAppointment({
            date: '',
            time: '',
            notes: '',
            type: 'Installation',
            assignedUser: '21'
          });
          setAppointmentDialogOpen(false);
          setSelectedProject(null);
          alert('Appointment booked successfully!');
        } else {
          const error = await calendarResponse.json();
          alert(`Failed to book appointment: ${error.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error booking appointment:', error);
        alert('An error occurred while booking the appointment.');
      }
    }
  };

  return (
    <Box>
      <Box sx={{
        mb: 3,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'flex-end',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2
      }}>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', sm: 'auto' } }}>
          <FormControl size="small" sx={{ minWidth: 120, width: { xs: '100%', sm: 'auto' } }}>
            <Select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
              displayEmpty
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.23)'
                }
              }}
            >
              <MenuItem value={0}>All Months</MenuItem>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => (
                <MenuItem key={month} value={month}>
                  {new Date(2000, month - 1, 1).toLocaleDateString('en-US', { month: 'long' })}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100, width: { xs: '100%', sm: 'auto' } }}>
            <Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              displayEmpty
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0, 0, 0, 0.23)'
                }
              }}
            >
              <MenuItem value={0}>All Years</MenuItem>
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Search Box - Mobile Only at Top */}
      <Box sx={{ display: { xs: 'block', md: 'none' }, mb: 2 }}>
        <TextField
          fullWidth
          placeholder="Search projects..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Status Summary Boxes */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          sm: 'repeat(3, 1fr)',
          md: 'repeat(5, 1fr)',
          lg: 'repeat(5, 1fr)'
        },
        gap: { xs: 1, sm: 1, md: 1 },
        mb: 2.5
      }}>
        {/* Planning */}
        <Card
          sx={{
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 3
            }
          }}
          onClick={() => setStatusFilter('planning')}
        >
          <CardContent sx={{ p: { xs: 0.75, sm: 1, md: 1 } }}>
            <Typography color="textSecondary" gutterBottom sx={{ fontSize: { xs: '0.55rem', sm: '0.6rem', md: '0.65rem' } }}>
              Planning
            </Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.3rem' }, color: '#ff9800' }}>
              {monthProjects.filter(p => p.status === 'planning').length}
            </Typography>
          </CardContent>
        </Card>

        {/* Delivery Ready */}
        <Card
          sx={{
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 3
            }
          }}
          onClick={() => setStatusFilter('delivery_ready')}
        >
          <CardContent sx={{ p: { xs: 0.75, sm: 1, md: 1 } }}>
            <Typography color="textSecondary" gutterBottom sx={{ fontSize: { xs: '0.55rem', sm: '0.6rem', md: '0.65rem' } }}>
              Delivery Ready
            </Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.3rem' }, color: '#3498DB' }}>
              {monthProjects.filter(p => p.status === 'delivery_ready').length}
            </Typography>
          </CardContent>
        </Card>

        {/* Installation */}
        <Card
          sx={{
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 3
            }
          }}
          onClick={() => setStatusFilter('installation')}
        >
          <CardContent sx={{ p: { xs: 0.75, sm: 1, md: 1 } }}>
            <Typography color="textSecondary" gutterBottom sx={{ fontSize: { xs: '0.55rem', sm: '0.6rem', md: '0.65rem' } }}>
              Installation
            </Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.3rem' }, color: '#9c27b0' }}>
              {monthProjects.filter(p => p.status === 'installation').length}
            </Typography>
          </CardContent>
        </Card>

        {/* Completed */}
        <Card
          sx={{
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 3
            }
          }}
          onClick={() => setStatusFilter('completed')}
        >
          <CardContent sx={{ p: { xs: 0.75, sm: 1, md: 1 } }}>
            <Typography color="textSecondary" gutterBottom sx={{ fontSize: { xs: '0.55rem', sm: '0.6rem', md: '0.65rem' } }}>
              Completed
            </Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.3rem' }, color: '#4caf50' }}>
              {monthProjects.filter(p => p.status === 'completed').length}
            </Typography>
          </CardContent>
        </Card>

        {/* Total */}
        <Card
          sx={{
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: 3
            }
          }}
          onClick={() => setStatusFilter('active')}
        >
          <CardContent sx={{ p: { xs: 0.75, sm: 1, md: 1 } }}>
            <Typography color="textSecondary" gutterBottom sx={{ fontSize: { xs: '0.55rem', sm: '0.6rem', md: '0.65rem' } }}>
              Total
            </Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.3rem' }, color: '#2196f3' }}>
              {monthProjects.filter(p => p.status !== 'remedial' && p.status !== 'remedial_resolved').length}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Filters Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' }, 
            gap: 2, 
            alignItems: { md: 'center' } 
          }}>
            {/* Search Box - Desktop Only */}
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' }, display: { xs: 'none', md: 'block' } }}>
              <TextField
                fullWidth
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 25%' } }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active (Not Completed)</MenuItem>
                  <MenuItem value="planning">Planning</MenuItem>
                  <MenuItem value="delivery_ready">Delivery Ready</MenuItem>
                  <MenuItem value="installation">Installation</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 25%' }, display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                {filteredProjects.length} projects found
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: { xs: 600, sm: 750, md: 900 } }}>
          <TableHead>
            <TableRow>
              <TableCell 
                onClick={() => handleSort('order_number')} 
                sx={{ 
                  cursor: 'pointer',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Order Number
                  {sortField === 'order_number' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell 
                onClick={() => handleSort('title')} 
                sx={{ 
                  cursor: 'pointer',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Project
                  {sortField === 'title' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell 
                onClick={() => handleSort('customer_name')} 
                sx={{ 
                  cursor: 'pointer',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' },
                  display: { xs: 'none', sm: 'table-cell' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Customer
                  {sortField === 'customer_name' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell 
                onClick={() => handleSort('status')} 
                sx={{ 
                  cursor: 'pointer',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Status
                  {sortField === 'status' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell 
                onClick={() => handleSort('material_cost')} 
                sx={{ 
                  cursor: 'pointer',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' },
                  display: { xs: 'none', md: 'table-cell' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Project Cost
                  {sortField === 'material_cost' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell 
                onClick={() => handleSort('start_date')} 
                sx={{ 
                  cursor: 'pointer',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' },
                  display: { xs: 'none', lg: 'table-cell' }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Dates
                  {sortField === 'start_date' && (
                    sortDirection === 'asc' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
                  )}
                </Box>
              </TableCell>
              <TableCell 
                align="right"
                sx={{ 
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  padding: { xs: '8px 4px', sm: '16px' }
                }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProjects
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((project) => (
              <TableRow 
                key={project.id}
                onClick={() => handleProjectClick(project.id)}
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                    backgroundColor: 'action.hover',
                  }
                }}
              >
                <TableCell>
                  <Typography variant="body2">
                    {project.order_number || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {project.title}
                  </Typography>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{project.customer_name}</TableCell>
                <TableCell>
                  <Chip
                    label={project.status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    size="small"
                    sx={getStatusColor(project.status)}
                  />
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  <Typography variant="body2">
                    {formatCurrency(
                      (project.material_cost ? parseFloat(project.material_cost.toString()) : 0) +
                      (project.fitter_balance ? parseFloat(project.fitter_balance.toString()) : 0) +
                      (project.gas_fitter_balance ? parseFloat(project.gas_fitter_balance.toString()) : 0)
                    )}
                  </Typography>
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                    <strong>Start:</strong> {project.start_date ? new Date(project.start_date).toLocaleDateString('en-GB') : 'Not set'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                    <strong>End:</strong> {project.end_date ? new Date(project.end_date).toLocaleDateString('en-GB') : 'Not set'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                    <strong>Delivery:</strong> {project.delivery_date ? new Date(project.delivery_date).toLocaleDateString('en-GB') : 'Not set'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    title="View Details"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleProjectClick(project.id);
                    }}
                  >
                    <Visibility />
                  </IconButton>
                  <IconButton
                    size="small"
                    title="Schedule Appointment"
                    onClick={(e) => handleScheduleClick(project, e)}
                  >
                    <CalendarToday />
                  </IconButton>
                  <IconButton
                    size="small"
                    title="Delete project"
                    onClick={(e) => handleDeleteClick(project, e)}
                    sx={{ color: 'error.main' }}
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={filteredProjects.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>
            Do you really want to delete this project?
          </Typography>
          {projectToDelete && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              {projectToDelete.title}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setDeleteDialogOpen(false);
              setProjectToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error"
            onClick={handleDeleteConfirm}
          >
            Delete Project
          </Button>
        </DialogActions>
      </Dialog>

      {/* Appointment Booking Dialog */}
      <Dialog open={appointmentDialogOpen} onClose={() => setAppointmentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Schedule Appointment for {selectedProject?.customer_name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Appointment Type</InputLabel>
                <Select
                  value={appointment.type}
                  onChange={(e) => setAppointment({ ...appointment, type: e.target.value })}
                  label="Appointment Type"
                >
                  <MenuItem value="Installation">Installation</MenuItem>
                  <MenuItem value="Delivery">Delivery</MenuItem>
                  <MenuItem value="Site Visit">Site Visit</MenuItem>
                  <MenuItem value="Remedial">Remedial</MenuItem>
                  <MenuItem value="Follow-up">Follow-up</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Assigned To</InputLabel>
                <Select
                  value={appointment.assignedUser}
                  onChange={(e) => setAppointment({ ...appointment, assignedUser: e.target.value })}
                  label="Assigned To"
                >
                  {users.map(user => (
                    <MenuItem key={user.id} value={user.id.toString()}>{user.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Appointment Date"
                type="date"
                value={appointment.date}
                onChange={(e) => setAppointment({ ...appointment, date: e.target.value })}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                required
              />
              <TextField
                fullWidth
                label="Time"
                type="time"
                value={appointment.time}
                onChange={(e) => setAppointment({ ...appointment, time: e.target.value })}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                required
              />
            </Box>

            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={appointment.notes}
              onChange={(e) => setAppointment({ ...appointment, notes: e.target.value })}
              variant="outlined"
              placeholder="Additional notes about the appointment..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAppointmentDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleBookAppointment}
            variant="contained"
            color="primary"
            disabled={!appointment.date || !appointment.time}
          >
            Book Appointment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}